import { useState } from "react";
import { ArrowRight, ClipboardCheck, Package, ScanLine } from "lucide-react";
import { packagingCost, roastYield } from "@anthemreactv2/roast-domain";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { InputField, Metrics, Notice, PageHeading, Panel } from "./components/workbench";

const initial = { greenWeight: "20", roastedWeight: "17", sampleWeight: "0.5", wasteWeight: "0.5", packWeight: "0.75", packs: "20", greenCostPerWeight: "4", roastLaborDollars: "12", packagingLaborDollars: "10", energyDollars: "4", packagingDollarsPerPack: "1", retailDollarsPerPack: "15" };
const groups = [
  { title: "Batch weights", description: "Net weights in pounds, before packaging.", fields: [
    { key: "greenWeight", label: "Green net weight (lb)", step: "0.01" },
    { key: "roastedWeight", label: "Roasted net weight (lb)", step: "0.01" },
    { key: "sampleWeight", label: "QC sample weight (lb)", step: "0.01" },
    { key: "wasteWeight", label: "Other waste (lb)", step: "0.01" },
  ] },
  { title: "Roasting costs", description: "Coffee, labor, and energy for the whole batch.", fields: [
    { key: "greenCostPerWeight", label: "Green cost ($/lb)", step: "0.01" },
    { key: "roastLaborDollars", label: "Roast labor, batch total ($)", step: "0.01" },
    { key: "energyDollars", label: "Energy, batch total ($)", step: "0.01" },
  ] },
  { title: "Packaging run", description: "Set pack size, quantity, and retail economics.", fields: [
    { key: "packWeight", label: "Coffee per pack (lb)", step: "0.01" },
    { key: "packs", label: "Packs to fill", step: "1" },
    { key: "packagingLaborDollars", label: "Packaging labor, run total ($)", step: "0.01" },
    { key: "packagingDollarsPerPack", label: "Materials per pack ($)", step: "0.01" },
    { key: "retailDollarsPerPack", label: "Retail price per pack ($)", step: "0.01" },
  ] },
] satisfies { title: string; description: string; fields: { key: keyof typeof initial; label: string; step: string }[] }[];

export function YieldCost() {
  const [values, setValues] = useState(initial);
  const [result, setResult] = useState<(ReturnType<typeof packagingCost> & { loss: number }) | null>(null);
  const [error, setError] = useState<string | null>(null);

  return (
    <>
      <PageHeading title="Yield & packaging" description="Measure roast loss and model the cost of each retail pack." actions={<Badge variant="outline">Calculation sandbox</Badge>} />
      <Notice title="Start with measured weights">These editable figures are training data. This calculator estimates yield and margin; it does not update stock or accounting.</Notice>
      <form className="space-y-5" onSubmit={event => {
        event.preventDefault();
        try {
          const input = Object.fromEntries(Object.entries(values).map(([key, value]) => [key, Number(value)])) as Parameters<typeof packagingCost>[0];
          setResult({ ...packagingCost(input), loss: (1 - roastYield(input.greenWeight, input.roastedWeight)) * 100 }); setError(null);
        } catch (error) { setResult(null); setError(error instanceof Error ? error.message : "Unable to calculate costs."); }
      }}>
        <div className="grid items-start gap-5 lg:grid-cols-3">
          {groups.map(group => <Panel key={group.title} title={group.title} description={group.description} className="h-full">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1">
              {group.fields.map(field => <InputField key={field.key} label={field.label} required type="number" min="0" step={field.step} value={values[field.key]} onChange={event => {
                setValues({ ...values, [field.key]: event.target.value }); setResult(null); setError(null);
              }} />)}
            </div>
          </Panel>)}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button type="submit">Calculate yield & margin<ArrowRight /></Button>
          <span className="text-xs text-muted-foreground">All costs are entered in dollars.</span>
        </div>
        {error && <Notice error>{error}</Notice>}
      </form>
      <Panel title="Batch economics" description="Costs and margin based on the weights and expenses above.">
        {result ? <div className="space-y-4" aria-live="polite">
          <Metrics items={[
            { label: "Measured roast loss", value: result.loss.toFixed(1), unit: "%", note: "Before QC samples and waste" },
            { label: "Modeled cost per pack", value: `$${result.costPerPack.toFixed(2)}`, note: "Includes entered labor and energy" },
            { label: "Modeled gross margin", value: result.marginPercent.toFixed(1), unit: "%", note: "(Price − cost) ÷ price" },
          ]} />
          <div className="flex items-start gap-3 rounded-lg bg-muted/40 p-3 text-sm">
            <Package aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
            <p><strong className="font-semibold tabular-nums">{result.remainingLooseWeight.toFixed(2)} lb</strong> remains loose after packing. QC samples and waste are removed once, before coffee cost is allocated.</p>
          </div>
          <p className="text-xs leading-relaxed text-muted-foreground">Freight, overhead, taxes, fees, discounts, and selling costs are included only if you add them to an appropriate cost input. This is a planning estimate.</p>
        </div> : <div className="rounded-lg border border-dashed px-4 py-7 text-center">
          <p className="text-sm font-medium">Calculate to see yield, pack cost, and margin</p>
          <p className="mt-1 text-xs text-muted-foreground">The result updates when you submit the form.</p>
        </div>}
      </Panel>
      <div className="grid gap-5 lg:grid-cols-2">
        <Panel title={<span className="flex items-center gap-2"><ClipboardCheck aria-hidden="true" className="size-4 text-muted-foreground" />Roast quality control</span>} action={<Badge variant="outline">Planned</Badge>}>
          <p className="text-sm leading-relaxed text-muted-foreground">Connect each roast to its pulled sample, cupping notes, evaluator, and QC decision. A score alone will not release coffee for sale.</p>
        </Panel>
        <Panel title={<span className="flex items-center gap-2"><ScanLine aria-hidden="true" className="size-4 text-muted-foreground" />Lot traceability</span>} action={<Badge variant="outline">Planned</Badge>}>
          <p className="text-sm leading-relaxed text-muted-foreground">Follow each green lot through roasting, blending, and packaging, with the allocations and losses recorded at every step.</p>
        </Panel>
      </div>
      <p className="text-xs leading-relaxed text-muted-foreground">Production yield estimates will need measured, QC-approved roasts with matching lots, profiles, machines, and batch sizes. Calculator edits are temporary and are not saved with the roast workspace.</p>
    </>
  );
}
